import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { api } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";
import { BlogHeader, BlogFooter } from "../components/blog/BlogHeader";
import { Markdown } from "../components/blog/Markdown";
import { SignupCTA } from "../components/blog/SignupCTA";
import { PostCard, formatDate } from "../components/blog/PostCard";

const useArticleSchema = (post) => {
  useEffect(() => {
    if (!post) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      image: post.cover_image ? [post.cover_image] : undefined,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      author: { "@type": "Organization", name: post.author || "Trackr" },
      publisher: { "@type": "Organization", name: "Trackr" },
      mainEntityOfPage: window.location.href,
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [post]);
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  usePageMeta(post ? `${post.title} — Trackr` : "Trackr Guides", post?.description);
  useArticleSchema(post);

  useEffect(() => {
    setPost(null);
    setMissing(false);
    window.scrollTo(0, 0);
    api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setMissing(true));
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#F4F1EE] text-[#1A362D] font-body">
      <BlogHeader />
      <main className="max-w-3xl mx-auto px-6 pt-12">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[#5C6B64] hover:text-[#C65D47] transition-colors" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4" /> All guides
        </Link>
        {missing && (
          <div className="py-24" data-testid="blog-post-not-found">
            <h1 className="font-display text-4xl">Guide not found</h1>
            <p className="text-[#5C6B64] mt-4">It may have moved. Browse all guides instead.</p>
          </div>
        )}
        {!post && !missing && <div className="mt-10 h-96 rounded-xl bg-[#E5E1DB] animate-pulse" />}
        {post && (
          <article data-testid="blog-post-article">
            <p className="mt-8 text-xs tracking-[0.2em] uppercase font-semibold text-[#C65D47]">{post.category}</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] font-medium mt-3" data-testid="blog-post-title">{post.title}</h1>
            <p className="text-base sm:text-lg text-[#5C6B64] mt-5 leading-relaxed">{post.description}</p>
            <div className="flex items-center gap-4 text-sm text-[#5C6B64] mt-6">
              <span>{post.author}</span>
              <span>·</span>
              <span>{formatDate(post.published_at)}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{post.read_minutes} min read</span>
            </div>
            {post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="w-full aspect-[16/9] object-cover rounded-xl mt-10" data-testid="blog-post-cover" />
            )}
            <div className="mt-10"><Markdown content={post.content} withCta /></div>
            <SignupCTA variant="end" />
          </article>
        )}
      </main>
      {post?.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-24" data-testid="blog-related">
          <h2 className="font-display text-2xl tracking-tight mb-8">Keep reading</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {post.related.map((p) => <PostCard key={p.slug} post={p} />)}
          </div>
        </section>
      )}
      <BlogFooter />
    </div>
  );
}
